/**
 * DialogManager - Centralized dialog management component
 * 
 * All dialogs in the application are rendered here based on the dialog ID
 * from the layout state. This ensures dialogs are not embedded within
 * individual components and provides a single source of truth for dialog rendering.
 */

import Dialog from "./dialog"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { RootState } from "@/state/store"

// Form imports
import DeleteCategoryForm from "@/features/storages/categories/deleteCategoryForm"
import CreateCategoryForm from "@/features/storages/categories/createCategoryForm"
import DeleteLocationForm from "@/features/storages/locations/deleteLocationForm"
import CreateLocationForm from "@/features/storages/locations/createLocationForm"
import CreateCustomerForm from "@/features/storages/customers/createCustomerForm"
import CreateSupplierForm from "@/features/storages/suppliers/createSupplierForm"
import AddToProductStockForm from "@/features/storages/products/actions/addToProductStockForm"
import RemoveFromProductStockForm from "@/features/storages/products/actions/removeFromProductStockForm"
import AdjustProductStockForm from "@/features/storages/products/actions/adjustProductStockForm"
import DeleteProductForm from "@/features/storages/products/deleteProductForm"
import DeleteNotificationForm from "@/components/notifications/deleteNotificationForm"
import DeleteActionForm from "@/features/storages/products/actions/deleteActionForm"
import ViewNotification from "@/components/notifications/ViewNotification"
import DeleteTransactionForm from "@/features/storages/transactions/deleteTransactionForm"
import GrantAccessForm from "@/features/storages/grantAccessForm"
import DeleteCustomerForm from "@/features/storages/customers/deleteCustomerForm"
import HelpAndSettings from "@/features/appSettings/helpAndSettings"
import AccountMenu from "@/features/appSettings/accountMenu"
import AddCustomInputGroupForm from "@/features/storages/categories/customInputs/addCustomInputGroupForm"
import AddCustomInputForm from "@/features/storages/categories/customInputs/addCustomInputForm"
import EditCustomInputGroupForm from "@/features/storages/categories/customInputs/editCustomInputGroupForm"
import DeleteCustomInputGroupForm from "@/features/storages/categories/customInputs/deleteCustomInputGroupForm"
import EditCustomInputForm from "@/features/storages/categories/customInputs/editCustomInputForm"

// Component imports
import HistoryList from "@/components/history/HistoryList"
import DeleteAccessDocForm from "@/components/access/DeleteAccessDocForm"
import RespondAccessInvitationForm from "@/components/access/RespondAccessInvitationForm"
import ActionBadge from "@/components/action/actionBadge"
import { ReactComponent as HistoryIcon } from '@/assets/icons/restore.svg'
import UserCard from "@/components/user/UserCard"
import TableList from "@/components/list/tableList/tableList"
import TableListRow from "@/components/list/tableList/tableListRow"
import TableListCell from "@/components/list/tableList/tableListCell"
import Image from "@/components/image/Image"
import Tag from "@/components/tags/tag"

// Hook imports
import useLayout from "@/hooks/useLayout"
import useActions from "@/hooks/useActions"
import useCustomers from "@/hooks/useCustomers"
import useNotifications from "@/hooks/useNotifications"
import { useQuery } from "@tanstack/react-query"
import { getProductDoc } from "@/services/firebaseApi"

// Util imports
import DateTime from "@/components/text/DateTime"

// Type imports
import { CustomInputGroups, ProductAiSuggestion, ProductRead } from "@/types"
import Search from "@/components/searchAndSelect/Search"
import ScanBarcodeForm from "@/features/storages/products/scanBarcodeForm"
import SuggestProductFromImagesForm from "@/features/storages/products/suggestProductFromImagesForm"

interface DialogManagerProps {
    storageId?: string;
    categoryId?: string;
    locationId?: string;
    customerId?: string;
    supplierId?: string;
    productId?: string;
    transactionId?: string;
}

const DialogManager: React.FC<DialogManagerProps> = ({ 
    storageId, 
    categoryId, 
    locationId,
    customerId,
    supplierId,
    productId,
    transactionId
}) => {
    const { t } = useTranslation()
    const { hideDialog } = useLayout()
    
    const { dialogQueue, dialogData } = useSelector((state: RootState) => state.layout)
    const deleteCustomerData = dialogData['delete_customer']
    const showActionData = dialogData['show_action']
    const viewNotificationId = dialogData['view_notification']?.notificationId as string | undefined
    const { notifications } = useNotifications()
    const viewNotification = viewNotificationId
        ? notifications.find((entry) => entry.id === viewNotificationId)
        : undefined

    const { customer } = useCustomers({ 
        storageId: deleteCustomerData?.storageId || storageId, 
        customerId: deleteCustomerData?.customerId || customerId 
    })

    const actionStorageId = showActionData?.storageId || storageId
    const actionProductId = showActionData?.productId || productId

    const { actions } = useActions({ 
        storageId: actionStorageId,
        productId: actionProductId
    })
    const { data: actionProduct, isLoading: actionProductLoading } = useQuery<ProductRead>({
        queryKey: ['product', actionStorageId, actionProductId],
        queryFn: () => getProductDoc({ storageId: actionStorageId!, productId: actionProductId! }),
        enabled: !!(actionStorageId && actionProductId),
        staleTime: 5 * 60 * 1000,
    })
    const focusProductAction = showActionData?.actionId 
        ? actions?.find(a => a.id === showActionData.actionId)
        : null

    if (!dialogQueue.length) return null

    const renderDialog = (currentDialogId: string) => {
    const currentDialogData = dialogData[currentDialogId]

    // Render the appropriate dialog based on ID
    // Search dialog
    if (currentDialogId === 'search' && storageId) {
        return (
            <Dialog mobileFullScreen={true} dialogId="search" desktopLarge={true} title={t('titleSearch')}>
                <Search 
                    storageId={storageId} 
                    categoryId={categoryId}
                    locationId={locationId}
                    customerId={customerId}
                    productId={productId}
                    displayBreadcrumbs={false}
                />
            </Dialog>
        )
    }

    // Stock action dialogs
    if (currentDialogId === 'add_to_stock' && storageId) {
        return (
            <Dialog title={t('add')} mobileFullScreen={true} dialogId="add_to_stock" colorScheme="green" desktopLarge={true}>
                <AddToProductStockForm 
                    storageId={currentDialogData?.storageId || storageId} 
                    productIdPreset={currentDialogData?.productId || productId}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'remove_from_stock' && storageId) {
        return (
            <Dialog title={t('remove')} mobileFullScreen={true} dialogId="remove_from_stock" colorScheme="red" desktopLarge={true}>
                <RemoveFromProductStockForm 
                    storageId={currentDialogData?.storageId || storageId} 
                    productIdPreset={currentDialogData?.productId || productId}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'adjust_product_stock' && storageId) {
        return (
            <Dialog title={t('adjustment')} mobileFullScreen={true} dialogId="adjust_product_stock" colorScheme="yellow" desktopLarge={true}>
                <AdjustProductStockForm 
                    storageId={currentDialogData?.storageId || storageId} 
                    productIdPreset={currentDialogData?.productId || productId}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'create_category' && (currentDialogData?.storageId || storageId)) {
        return (
            <Dialog title={t('titleCreateCategory')} dialogId="create_category" mobileFullScreen={true} desktopLarge={true}>
                <CreateCategoryForm
                    storageId={(currentDialogData?.storageId as string) || storageId!}
                    defaultParentCategory={currentDialogData?.parentCategoryId as string | undefined}
                    onSuccess={hideDialog}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'create_location' && (currentDialogData?.storageId || storageId)) {
        return (
            <Dialog title={t('titleCreateLocation')} dialogId="create_location" mobileFullScreen={true} desktopLarge={true}>
                <CreateLocationForm
                    storageId={(currentDialogData?.storageId as string) || storageId!}
                    defaultParentLocation={currentDialogData?.parentLocationId as string | undefined}
                    onSuccess={hideDialog}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'create_supplier' && (currentDialogData?.storageId || storageId)) {
        return (
            <Dialog title={t('titleCreateSupplier')} dialogId="create_supplier" mobileFullScreen={true} desktopLarge={true}>
                <CreateSupplierForm
                    storageId={(currentDialogData?.storageId as string) || storageId!}
                    onSuccess={(createdId) => {
                        const onCreated = currentDialogData?.onCreated as ((id: string) => void) | undefined
                        if (createdId && onCreated) onCreated(createdId)
                        hideDialog()
                    }}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'create_customer' && (currentDialogData?.storageId || storageId)) {
        return (
            <Dialog title={t('titleCreateCustomer')} dialogId="create_customer" mobileFullScreen={true} desktopLarge={true}>
                <CreateCustomerForm
                    storageId={(currentDialogData?.storageId as string) || storageId!}
                    onSuccess={(createdId) => {
                        const onCreated = currentDialogData?.onCreated as ((id: string) => void) | undefined
                        if (createdId && onCreated) onCreated(createdId)
                        hideDialog()
                    }}
                />
            </Dialog>
        )
    }

    // Delete dialogs
    if (currentDialogId === 'delete_category' && storageId && (categoryId || currentDialogData?.categoryId)) {
        return (
            <Dialog title={t('deleteCategory')} dialogId="delete_category" colorScheme="red" displayCloseButton={false}>
                <DeleteCategoryForm 
                    storageId={currentDialogData?.storageId || storageId} 
                    categoryId={currentDialogData?.categoryId || categoryId!} 
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_location' && storageId && (locationId || currentDialogData?.locationId)) {
        return (
            <Dialog title={t('deleteLocation')} dialogId="delete_location" colorScheme="red" displayCloseButton={false}>
                <DeleteLocationForm 
                    storageId={currentDialogData?.storageId || storageId} 
                    locationId={currentDialogData?.locationId || locationId!} 
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_notification' && currentDialogData?.notificationId) {
        return (
            <Dialog title={t('titleDeleteNotification')} mobileFullScreen={true} dialogId="delete_notification" colorScheme="red" displayCloseButton={false}>
                <DeleteNotificationForm notificationId={currentDialogData.notificationId as string} />
            </Dialog>
        )
    }

    if (currentDialogId === 'view_notification' && currentDialogData?.notificationId) {
        return (
            <Dialog title={viewNotification?.title || viewNotification?.id || t('titleNotification')} mobileFullScreen={true} dialogId="view_notification" className="lg:w-[min(40rem,calc(100%-1rem))]">
                <ViewNotification notificationId={currentDialogData.notificationId as string} />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_product' && storageId && (productId || currentDialogData?.productId)) {
        return (
            <Dialog title={t('titleDeleteProduct')} mobileFullScreen={true} dialogId="delete_product" colorScheme="red" displayCloseButton={false}>
                <DeleteProductForm 
                    storageId={currentDialogData?.storageId || storageId} 
                    productId={currentDialogData?.productId || productId!} 
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_action' && currentDialogData?.storageId && currentDialogData?.productId && currentDialogData?.actionId) {
        return (
            <Dialog title={t('titleDeleteAction')} mobileFullScreen={true} dialogId="delete_action" colorScheme="red" displayCloseButton={false}>
                <DeleteActionForm
                    storageId={currentDialogData.storageId as string}
                    productId={currentDialogData.productId as string}
                    actionId={currentDialogData.actionId as string}
                    transactionDocId={currentDialogData.transactionDocId as string | undefined}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_transaction' && storageId && (transactionId || currentDialogData?.transactionId)) {
        return (
            <Dialog title={t('titleDeleteTransaction')} mobileFullScreen={true} dialogId="delete_transaction" colorScheme="red" displayCloseButton={false}>
                <DeleteTransactionForm
                    storageId={currentDialogData?.storageId || storageId}
                    transactionId={currentDialogData?.transactionId || transactionId!}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_customer' && customer && storageId && customerId) {
        return (
            <Dialog
                dialogId="delete_customer"
                title={t('titleDeleteCustomer')}
                colorScheme="red"
                mobileFullScreen={true}
                displayCloseButton={false}
            >
                <DeleteCustomerForm
                    customer={customer}
                    storageId={storageId}
                    customerId={customerId}
                    onCancel={hideDialog}
                    onSuccess={hideDialog}
                />
            </Dialog>
        )
    }

    // Access management dialogs
    if (currentDialogId === 'dialogGrantAccess' && storageId) {
        return (
            <Dialog dialogId="dialogGrantAccess" title={t('titleGrantAccess')}>
                <GrantAccessForm storageId={storageId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'delete_access' && currentDialogData?.storageId && currentDialogData?.accessId) {
        return (
            <Dialog title={t('titleDeleteAccess')} mobileFullScreen={true} dialogId="delete_access" colorScheme="red" displayCloseButton={false}>
                <DeleteAccessDocForm storageId={currentDialogData.storageId} accessId={currentDialogData.accessId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'respond_access' && currentDialogData?.storageId && currentDialogData?.accessId) {
        const accept = Boolean(currentDialogData.accept)
        return (
            <Dialog
                title={t(accept ? 'titleAcceptAccess' : 'titleDeclineAccess')}
                mobileFullScreen={true}
                dialogId="respond_access"
                colorScheme={accept ? 'green' : 'red'}
                displayCloseButton={false}
            >
                <RespondAccessInvitationForm
                    storageId={currentDialogData.storageId}
                    accessId={currentDialogData.accessId}
                    accept={accept}
                />
            </Dialog>
        )
    }

    // History dialogs
    if (currentDialogId === 'global_history' && storageId) {
        return (
            <Dialog title={t('history')} dialogId="global_history">
                <HistoryList 
                    storageId={storageId} 
                    categoryId={categoryId} 
                    customerId={customerId} 
                    productId={productId} 
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'product_history' && storageId && productId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="product_history" desktopLarge={true}>
                <HistoryList storageId={storageId} productId={productId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'category_history' && storageId && categoryId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="category_history" desktopLarge={true}>
                <HistoryList storageId={storageId} categoryId={categoryId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'location_history' && storageId && locationId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="location_history" desktopLarge={true}>
                <HistoryList storageId={storageId} locationId={locationId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'customer_history' && storageId && customerId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="customer_history" desktopLarge={true}>
                <HistoryList storageId={storageId} customerId={customerId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'supplier_history' && storageId && supplierId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="supplier_history" desktopLarge={true}>
                <HistoryList storageId={storageId} supplierId={supplierId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'transaction_history' && storageId && transactionId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="transaction_history" desktopLarge={true}>
                <HistoryList storageId={storageId} transactionId={transactionId} />
            </Dialog>
        )
    }

    if (currentDialogId === 'storage_history' && storageId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="storage_history" desktopLarge={true}>
                <HistoryList storageId={storageId} />
            </Dialog>
        )
    }

    // Action dialogs
    if (currentDialogId === 'show_action' && focusProductAction && actions) {
        const description = focusProductAction.description?.trim()
        return (
            <Dialog title={t('action')} dialogId="show_action">
                <TableList width="fit">
                    <TableListRow hoverEffect={false}>
                        <TableListCell isLabel={true}><b>{t('action')}</b></TableListCell>
                        <TableListCell>
                            <ActionBadge type={focusProductAction.type as string} amount={focusProductAction.amount} />
                            {focusProductAction.isDraft && <Tag colorScheme="yellow">{t('tagDraft')}</Tag>}
                        </TableListCell>
                    </TableListRow>
                    <TableListRow hoverEffect={false}>
                        <TableListCell isLabel={true}><b>{t('labelProduct')}</b></TableListCell>
                        <TableListCell>
                            {actionProduct ? (
                                <Link
                                    to={`/storage/products/${actionProduct.id}`}
                                    className="flex items-center gap-2"
                                >
                                    <Image
                                        src={actionProduct.images?.[0]?.url}
                                        alt={actionProduct.name}
                                        size="sm"
                                    />
                                    <div>
                                        <div className="font-medium">{actionProduct.name}</div>
                                        <div className="text-sm text-gray-500">{actionProduct.sku}</div>
                                    </div>
                                </Link>
                            ) : actionProductLoading ? (
                                <div className="skeleton" />
                            ) : (
                                <span>-</span>
                            )}
                        </TableListCell>
                    </TableListRow>
                    <TableListRow hoverEffect={false}>
                        <TableListCell isLabel={true}><b>{t('name')}</b></TableListCell>
                        <TableListCell>
                            {focusProductAction.createdBy ? (
                                <UserCard userId={focusProductAction.createdBy} size="small" />
                            ) : (
                                <span>{t('unknown')}</span>
                            )}
                        </TableListCell>
                    </TableListRow>
                    <TableListRow hoverEffect={false}>
                        <TableListCell isLabel={true}><b>{t('timestamp')}</b></TableListCell>
                        <TableListCell>
                            <DateTime timestamp={focusProductAction.keyHistory?.createdAt} />
                        </TableListCell>
                    </TableListRow>
                    <TableListRow hoverEffect={false}>
                        <TableListCell isLabel={true}><b>{t('description')}</b></TableListCell>
                        <TableListCell>{description || '-'}</TableListCell>
                    </TableListRow>
                </TableList>
            </Dialog>
        )
    }

    if (currentDialogId === 'action_history' && currentDialogData?.actionId) {
        return (
            <Dialog title={t('documentHistory')} dialogId="action_history">
                <HistoryList 
                    storageId={currentDialogData.storageId} 
                    productId={currentDialogData.productId}
                    actionId={currentDialogData.actionId}
                />
            </Dialog>
        )
    }

    if (currentDialogId === 'help_and_settings') {
        return (
            <Dialog title={t('titleHelpAndSettings')} dialogId="help_and_settings" className="w-[min(40rem,calc(100%-1rem))]">
                <HelpAndSettings />
            </Dialog>
        )
    }

    if (currentDialogId === 'account') {
        return (
            <Dialog title={t('titleAccount')} dialogId="account">
                <AccountMenu />
            </Dialog>
        )
    }

    if (currentDialogId === 'scan_barcode') {
        return (
            <Dialog title={t('titleScanBarcode')} dialogId="scan_barcode" mobileFullScreen={true}>
                <ScanBarcodeForm />
            </Dialog>
        )
    }

    if (currentDialogId === 'suggest_product_from_images' && (currentDialogData?.storageId || storageId)) {
        return (
            <Dialog title={t('titleSuggestProductFromImages')} dialogId="suggest_product_from_images" mobileFullScreen={true} desktopLarge={true}>
                <SuggestProductFromImagesForm
                    storageId={(currentDialogData?.storageId as string) || storageId!}
                    onSuccess={currentDialogData?.onSuccess as ((payload: { files: File[]; suggestion: ProductAiSuggestion }) => void) | undefined}
                />
            </Dialog>
        )
    }

    // Custom input dialogs (for category custom fields)
    if (currentDialogId === 'add_custom_field_group' && currentDialogData) {
        return (
            <Dialog title={t('titleAddGroup')} dialogId="add_custom_field_group">
                <AddCustomInputGroupForm 
                    groups={currentDialogData.groups as CustomInputGroups} 
                    setGroups={currentDialogData.setGroups} 
                    setCustomInputGroupOrder={currentDialogData.setCustomInputGroupOrder} 
                />
            </Dialog>
        )
    }

    // Dynamic custom input dialogs (with template string IDs)
    if (currentDialogId.startsWith('add_custom_field_') && currentDialogData?.groupName) {
        const groupName = currentDialogData.groupName
        return (
            <Dialog title={`${t('titleAddInput')} - ${groupName}`} mobileFullScreen={true} dialogId={currentDialogId}>
                <AddCustomInputForm 
                    groupName={groupName} 
                    setGroups={currentDialogData.setGroups} 
                    groups={currentDialogData.groups as CustomInputGroups} 
                />
            </Dialog>
        )
    }

    if (currentDialogId.startsWith('edit_custom_field_group_') && currentDialogData?.groupName) {
        const groupName = currentDialogData.groupName
        return (
            <Dialog title={`${t('titleEditGroup')} - ${groupName}`} mobileFullScreen={true} dialogId={currentDialogId}>
                <EditCustomInputGroupForm 
                    groups={currentDialogData.groups as CustomInputGroups} 
                    setGroups={currentDialogData.setGroups} 
                    setCustomInputGroupOrder={currentDialogData.setCustomInputGroupOrder} 
                    groupName={groupName} 
                />
            </Dialog>
        )
    }

    if (currentDialogId.startsWith('delete_custom_field_group_') && currentDialogData?.groupName) {
        const groupName = currentDialogData.groupName
        return (
            <Dialog title={`${t('titleDeleteGroup')} - ${groupName}`} mobileFullScreen={true} dialogId={currentDialogId}>
                <DeleteCustomInputGroupForm 
                    groups={currentDialogData.groups as CustomInputGroups} 
                    setGroups={currentDialogData.setGroups} 
                    setCustomInputGroupOrder={currentDialogData.setCustomInputGroupOrder} 
                    groupName={groupName} 
                />
            </Dialog>
        )
    }

    // Handle edit_custom_field_{groupName}_{inputKey} pattern
    if (currentDialogId.match(/^edit_custom_field_(.+)_(.+)$/) && currentDialogData) {
        const matches = currentDialogId.match(/^edit_custom_field_(.+)_(.+)$/)
        if (matches) {
            const [, groupName, inputKey] = matches
            return (
                <Dialog title={`${t('editInput')} - ${inputKey}`} mobileFullScreen={true} dialogId={currentDialogId}>
                    <EditCustomInputForm
                        customInputKey={inputKey}
                        groups={currentDialogData.groups as CustomInputGroups}
                        groupName={groupName}
                        setGroups={currentDialogData.setGroups}
                    />
                </Dialog>
            )
        }
    }

    // Fallback - dialog ID not recognized
    return null
    }

    return (
        <>
            {dialogQueue.map((dialogId) => (
                <div key={dialogId}>
                    {renderDialog(dialogId)}
                </div>
            ))}
        </>
    )
}

export default DialogManager
